<template>
  <div class="mobile-upload-container">
    <div class="upload-card">
      <div class="header">
        <div class="logo">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
        </div>
        <h1>{{ $t('mobileUploadView.title') }}</h1>
        <p class="subtitle">
          {{ $t('mobileUploadView.subtitlePrefix') }}<span class="countdown" v-if="expiresIn > 0">{{ formatTime(expiresIn) }}</span>{{ $t('mobileUploadView.subtitleSuffix') }}
        </p>
      </div>

      <div v-if="checking" class="status-view">
        <div class="spinner"></div>
        <p>{{ $t('mobileUploadView.checking') }}</p>
      </div>

      <div v-else-if="isExpired" class="status-view expired">
        <div class="error-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
        </div>
        <h2>{{ $t('mobileUploadView.expiredTitle') }}</h2>
        <p>{{ $t('mobileUploadView.expiredDesc') }}</p>
      </div>

      <div v-else class="upload-area" 
        @dragover.prevent="isDragging = true" 
        @dragleave.prevent="isDragging = false" 
        @drop.prevent="handleDrop"
        :class="{ 'is-dragging': isDragging, 'is-uploading': uploading }"
      >
        <div class="drop-zone">
          <div class="icon-stack">
            <svg class="cloud-icon" viewBox="0 0 1024 1024" width="64" height="64" fill="currentColor"><path d="M795.128 246.24C745.64 129.752 631.624 52.744 505.832 52.744c-171.656 0-311.6 139.832-314.384 311.784C83.504 379.464 0 474.928 0 590.144c0 125.544 100.184 227.688 223.336 227.688h76.984a30.32 30.32 0 0 0 30.344-30.344 30.32 30.32 0 0 0-30.344-30.344h-76.984c-89.696 0-162.648-74.904-162.648-167 0-93.608 70.408-166.944 160.312-166.944a30.344 30.344 0 0 0 30.312-29.304l0.768-23.056c0-141.936 113.84-257.408 253.76-257.408 106.408 0 202.448 68.568 238.976 170.648a30.344 30.344 0 0 0 25.512 19.976c110.016 11.112 192.984 108.304 192.984 226.032 0 125.184-95.768 227.064-213.464 227.064H718.08a30.32 30.32 0 0 0-30.344 30.344 30.32 30.32 0 0 0 30.344 30.344h31.768c151.176 0 274.152-129.08 274.152-287.752 0-142.36-97.136-261.096-228.872-283.848z"></path><path d="M545.664 575.528a29.88 29.88 0 0 0-9.896-6.576 30.128 30.128 0 0 0-23.168 0 29.84 29.84 0 0 0-9.896 6.576L393.208 684.96a30.336 30.336 0 1 0 42.912 42.904l57.72-57.696v270.744c0 16.776 13.568 30.344 30.344 30.344s30.344-13.568 30.344-30.344V670.2l57.664 57.664a30.32 30.32 0 0 0 42.896 0 30.32 30.32 0 0 0 0-42.904L545.664 575.528z"></path></svg>
          </div>
          <div class="text-group">
            <h3>{{ $t(uploading ? 'mobileUploadView.uploading' : 'mobileUploadView.dropHere') }}</h3>
            <p v-if="!uploading">{{ $t('mobileUploadView.supportedFormats') }}</p>
            <div v-else class="progress-bar-container">
              <div class="progress-bar" :style="{ width: uploadProgress + '%' }"></div>
            </div>
          </div>
          <input type="file" class="file-input" @change="handleFileSelect" :disabled="uploading" />
        </div>

        <div v-if="uploadSuccess" class="success-tip">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
          <span>{{ $t('mobileUploadView.uploadSuccess') }}</span>
        </div>
      </div>

      <div class="footer-actions" v-if="!isExpired && !checking">
        <button class="copy-link-btn" @click="copyLink">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
          <span>{{ $t('mobileUploadView.copyLink') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { checkUploadKey, uploadKnowledge } from '@/api/kb';
import { showToast } from '@/utils/toast';

export default {
  name: 'MobileUploadView',
  props: {
    uploadKey: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      checking: true,
      isExpired: false,
      uploading: false,
      uploadProgress: 0,
      isDragging: false,
      uploadSuccess: false,
      expiresIn: 0,
      timer: null
    };
  },
  async mounted() {
    await this.verifyKey();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    startTimer() {
      this.stopTimer();
      this.timer = setInterval(() => {
        if (this.expiresIn > 0) {
          this.expiresIn--;
        } else {
          this.isExpired = true;
          this.stopTimer();
        }
      }, 1000);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    formatTime(seconds) {
      const m = Math.floor(seconds / 60);
      const s = (seconds % 60).toString().padStart(2, '0');
      return this.$t('mobileUploadView.timeFormat', { minutes: m, seconds: s });
    },
    async verifyKey() {
      this.checking = true;
      try {
        const res = await checkUploadKey(this.uploadKey);
        if (res.code === 200 && res.data) {
          this.expiresIn = res.data.expires_in || 0;
          if (this.expiresIn > 0) {
            this.startTimer();
          } else {
            this.isExpired = true;
          }
        } else {
          this.isExpired = true;
        }
      } catch (err) {
        console.error('Verify key failed:', err);
        this.isExpired = true;
      } finally {
        this.checking = false;
      }
    },
    async handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) await this.uploadFile(file);
    },
    async handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) await this.uploadFile(file);
    },
    async uploadFile(file) {
      if (this.uploading || this.isExpired) return;
      
      this.uploading = true;
      this.uploadSuccess = false;
      this.uploadProgress = 10;
      
      try {
        // 使用 key 换取上传权限，后端应该支持通过 key 上传
        const res = await uploadKnowledge(this.uploadKey, 2, { file, isKeyUpload: true });
        if (res.code === 200) {
          this.uploadProgress = 100;
          this.uploadSuccess = true;
          showToast(this.$t('mobileUploadView.uploadSuccess'), 'success');
          setTimeout(() => {
            this.uploadSuccess = false;
            this.uploadProgress = 0;
          }, 3000);
        } else {
          alert(this.$t('mobileUploadView.uploadFailed', { message: res.message }));
        }
      } catch (err) {
        console.error('Upload failed:', err);
        alert(this.$t('mobileUploadView.uploadError'));
      } finally {
        this.uploading = false;
      }
    },
    copyLink() {
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        alert(this.$t('mobileUploadView.copyLinkSuccess'));
      });
    }
  }
};
</script>

<style scoped>
.mobile-upload-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f6f8ff 0%, #ffffff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.upload-card {
  width: 100%;
  max-width: 500px;
  background: #ffffff;
  border-radius: 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.05);
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.logo {
  width: 64px;
  height: 64px;
  background: #EEF2FF;
  color: #1A3BFF;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

h1 {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  margin: 0;
}

.subtitle {
  font-size: 14px;
  color: #6B7280;
  line-height: 1.6;
  max-width: 300px;
}

.countdown {
  color: #1A3BFF;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.status-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  gap: 16px;
  color: #6B7280;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top-color: #1A3BFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.expired h2 {
  color: #EF4444;
  margin: 0;
}

.error-icon {
  color: #EF4444;
  background: #FEF2F2;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.upload-area {
  position: relative;
  border: 2px dashed #E5E7EB;
  border-radius: 24px;
  padding: 40px 20px;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.upload-area:hover, .upload-area.is-dragging {
  border-color: #1A3BFF;
  background-color: #F8FAFF;
}

.upload-area.is-uploading {
  border-style: solid;
  border-color: #E5E7EB;
  pointer-events: none;
}

.drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
}

.icon-stack {
  color: #1A3BFF;
  transition: transform 300ms;
}

.upload-area:hover .icon-stack {
  transform: translateY(-5px);
}

.text-group h3 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
}

.text-group p {
  font-size: 13px;
  color: #9CA3AF;
  margin: 0;
}

.file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.progress-bar-container {
  width: 200px;
  height: 6px;
  background: #F3F4F6;
  border-radius: 9999px;
  overflow: hidden;
  margin-top: 12px;
}

.progress-bar {
  height: 100%;
  background: #1A3BFF;
  transition: width 300ms;
}

.success-tip {
  position: absolute;
  bottom: -40px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #10B981;
  font-size: 14px;
  font-weight: 600;
  animation: slideUp 300ms ease-out;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.footer-actions {
  display: flex;
  justify-content: center;
}

.copy-link-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #F3F4F6;
  border: none;
  padding: 12px 24px;
  border-radius: 14px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 200ms;
}

.copy-link-btn:hover {
  background: #E5E7EB;
  color: #111827;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .upload-card {
    padding: 30px 20px;
    box-shadow: none;
    background: transparent;
  }
}
</style>
